// pages/AuditAdmin.jsx — Admin Board tab: set the audit credit price, grant pilot credits, and end an
// auditor's declaration if KaiLux is told they are no longer eligible. KaiLux does NOT verify
// auditors and has no review queue: the auditor declares their own standing.
import { useEffect, useState } from 'react'
import { adminRevokeAuditCredential, adminGetAuditPricing, adminSetAuditPricing, adminGrantAuditCredits } from '../api/kailux'
import { T } from '../theme/theme'
import { Card, Field, Notice, btn, inputStyle } from '../components/audit/ui'
import { auditErrorText, formatRand } from '../utils/audit'

export default function AuditAdmin() {
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [price, setPrice] = useState(null)
  const [priceInput, setPriceInput] = useState('')

  useEffect(() => { adminGetAuditPricing().then((r) => { setPrice(r.price_per_credit_rand); setPriceInput(r.price_per_credit_rand ?? '') }).catch(() => {}) }, [])

  const savePrice = async () => {
    setError(''); setInfo('')
    try { const r = await adminSetAuditPricing(Number(priceInput)); setPrice(r.price_per_credit_rand); setInfo('Price saved.') }
    catch (e) { setError(auditErrorText(e)) }
  }

  return (
    <div data-testid="audit-admin">
      {error && <Notice tone="bad" testId="audit-admin-error">{error}</Notice>}
      {info && <Notice tone="ok" testId="audit-admin-info">{info}</Notice>}
      <Card testId="pricing-card" title="Audit credit price" subtitle="Charged upfront per credit. Until a price is set, firms cannot buy credits.">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ width: '200px' }}><Field label="Price per credit (Rand)">
            <input aria-label="Price per credit" type="number" min="0" step="0.01" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} style={inputStyle} /></Field></div>
          <button type="button" style={btn(true, !(Number(priceInput) > 0))} disabled={!(Number(priceInput) > 0)} onClick={savePrice}>Save price</button>
          <span data-testid="current-price" style={{ fontSize: '13px', color: T.mt }}>Current: {price ? formatRand(price) : 'not set'}</span>
        </div>
      </Card>
      <GrantCard onError={setError} onInfo={setInfo} />
      <RevokeCard onError={setError} onInfo={setInfo} />
    </div>
  )
}

function RevokeCard({ onError, onInfo }) {
  const [id, setId] = useState('')
  const [reason, setReason] = useState('')
  const ok = id.trim() && reason.trim().length >= 5
  const revoke = async () => {
    try { await adminRevokeAuditCredential(id.trim(), reason.trim()); onInfo('Declaration ended.'); onError(''); setReason(''); setId('') }
    catch (e) { onError(auditErrorText(e)) }
  }
  return (
    <Card testId="revoke-card" title="End an auditor's declaration"
      subtitle="Exception tool, not a review step. KaiLux does not verify auditors. Use this only if you are told a person is no longer eligible; the reason is recorded.">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '12px', alignItems: 'end' }}>
        <Field label="Credential ID"><input aria-label="Credential ID" value={id} onChange={(e) => setId(e.target.value)} style={inputStyle} /></Field>
        <Field label="Reason (recorded)"><input aria-label="Revoke reason" value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} /></Field>
        <button type="button" disabled={!ok} style={btn(false, !ok)} onClick={revoke}>End declaration</button>
      </div>
    </Card>
  )
}

function GrantCard({ onError, onInfo }) {
  const [firmId, setFirmId] = useState('')
  const [credits, setCredits] = useState(1)
  const [reason, setReason] = useState('')
  const ok = firmId.trim() && Number(credits) >= 1 && reason.trim().length >= 10
  const grant = async () => {
    try { await adminGrantAuditCredits(firmId.trim(), Number(credits), reason.trim()); onInfo('Credits granted.'); onError(''); setReason('') }
    catch (e) { onError(auditErrorText(e)) }
  }
  return (
    <Card testId="grant-card" title="Grant pilot credits" subtitle="For pilot firms. Recorded in the ledger with your reason.">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '12px', alignItems: 'end' }}>
        <Field label="Firm ID"><input aria-label="Firm ID" value={firmId} onChange={(e) => setFirmId(e.target.value)} style={inputStyle} /></Field>
        <Field label="Credits"><input aria-label="Credits" type="number" min="1" max="100" value={credits} onChange={(e) => setCredits(e.target.value)} style={inputStyle} /></Field>
        <Field label="Reason (at least 10 characters)"><input aria-label="Grant reason" value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} /></Field>
        <button type="button" disabled={!ok} style={btn(true, !ok)} onClick={grant}>Grant</button>
      </div>
    </Card>
  )
}
